"use client";

import { useCallback, useMemo, useState } from "react";
import { Button, SelectInput } from "@dionatanbig2be/big2be-ui";
import { FiMapPin } from "react-icons/fi";

import { useAppBoot, useAvailableStores } from "@/shell/hooks/useBootstrap";
import { readSelectedStoreId } from "../lib/selected-store";
import { useActiveStore } from "../stores/useActiveStore";
import { useAuthStore } from "@/modules/auth/stores/useAuthStore";
import { updateCustomerProfile } from "@/modules/profile";
import { nearestStore } from "../lib/nearest-store";

import { Modal } from "@/shared/components/Modal";
import { useStoreSwitcher } from "../stores/useStoreSwitcher";
import { useSwitchStore } from "../hooks/useSwitchStore";

/** Loja pré-selecionada ao abrir: a ativa, senão a do cookie. */
function initialSelectedId(): string {
    const current = useActiveStore.getState().storeId || readSelectedStoreId();
    return current ? String(current) : "";
}

/**
 * Estado do FORMULÁRIO. Vive num componente que só monta com o modal aberto
 * (ver `StoreSwitcherModal`): cada abertura remonta e recomeça do zero, sem
 * precisar de um efeito para "resetar quando abrir".
 */
function useStoreSwitcherForm(onClose: () => void) {
    const stores = useAvailableStores();
    const user = useAuthStore((s) => s.user);
    const switchStore = useSwitchStore();

    const [selectedId, setSelectedId] = useState<string>(initialSelectedId);
    const [isGpsLoading, setIsGpsLoading] = useState(false);
    const [gpsError, setGpsError] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const options = useMemo(
        () =>
            stores.map((store) => ({
                value: String(store.store_id),
                label: store.description,
                description: store.address,
            })),
        [stores]
    );

    const handleGps = useCallback(() => {
        if (!navigator.geolocation) {
            setGpsError("Geolocalização não suportada neste navegador.");
            return;
        }

        setIsGpsLoading(true);
        setGpsError("");

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const nearest = nearestStore(stores, pos.coords.latitude, pos.coords.longitude);
                if (nearest) {
                    setSelectedId(String(nearest.store.store_id));
                } else {
                    setGpsError("Nenhuma loja próxima encontrada.");
                }
                setIsGpsLoading(false);
            },
            () => {
                setGpsError("Não foi possível obter sua localização.");
                setIsGpsLoading(false);
            },
            { timeout: 10_000, maximumAge: 60_000 }
        );
    }, [stores]);

    const handleSave = useCallback(() => {
        const id = Number(selectedId);
        if (!id) return;

        setIsSaving(true);

        // Fire-and-forget: a favorita é conveniência para o próximo login, não
        // pré-requisito da troca. Falha não bloqueia nem avisa.
        if (user) {
            updateCustomerProfile(user.cpfCnpj, { favoriteStore: id }).catch(() => {});
        }

        onClose();
        // Cookie + estado + router.refresh(): a página não recarrega, o servidor
        // re-resolve tema/settings/rodapé e o carrinho re-chaveia pela loja.
        switchStore(id);
    }, [selectedId, user, onClose, switchStore]);

    return {
        options,
        selectedId,
        setSelectedId,
        isGpsLoading,
        gpsError,
        isSaving,
        handleGps,
        handleSave,
    };
}

function StoreSwitcherForm({ required, onClose }: { required: boolean; onClose: () => void }) {
    const {
        options,
        selectedId,
        setSelectedId,
        isGpsLoading,
        gpsError,
        isSaving,
        handleGps,
        handleSave,
    } = useStoreSwitcherForm(onClose);

    return (
        <div className="flex flex-col">
            <div className="mb-7 text-center">
                <h2 className="text-2xl font-bold text-base-content">Escolha sua loja favorita</h2>
                <p className="mt-2 text-sm leading-relaxed text-base-content/60">
                    As entregas estão disponíveis em um raio de 20 km da loja selecionada.
                </p>
            </div>

            {required && (
                <p className="mb-4 text-center text-sm font-medium text-warning">
                    Selecione uma loja para continuar.
                </p>
            )}

            <SelectInput
                aria-label="Selecione a loja"
                placeholder="Selecione a loja"
                options={options}
                value={selectedId}
                onChange={(value) => setSelectedId(value ?? "")}
                triggerClassName="h-10 rounded-xl border-base-300"
            />

            <Button
                type="button"
                tone="primary"
                size="lg"
                width="block"
                isLoading={isSaving}
                disabled={!selectedId || isSaving}
                onClick={handleSave}
                className="mt-5 rounded-xl text-lg"
            >
                Salvar loja
            </Button>

            <Button
                type="button"
                tone="default"
                variant="ghost"
                isLoading={isGpsLoading}
                disabled={isGpsLoading}
                onClick={handleGps}
                className="mt-3 justify-center text-base-content/55 hover:text-base-content"
            >
                <FiMapPin size={18} />
                Usar serviço de GPS
            </Button>

            {gpsError && <p className="mt-2 text-center text-xs text-error">{gpsError}</p>}
        </div>
    );
}

/**
 * Modal de troca de loja. Só existe quando o app_boot permite a troca
 * (`can_change_store`): sem isso o servidor ignora o cookie, e "salvar" aqui
 * só fingiria uma troca que o próximo render desfaz.
 */
export function StoreSwitcherModal() {
    const canChange = useAppBoot()?.can_change_store ?? false;
    const { open, close, required } = useStoreSwitcher();
    const isOpen = open && canChange;

    const handleClose = useCallback(() => {
        if (!required) close();
    }, [close, required]);

    return (
        <Modal
            open={isOpen}
            onClose={handleClose}
            title={<span className="text-secondary">Bem-vindo!</span>}
            showCloseButton={!required}
            ariaLabel="Selecionar loja"
            boxClassName="max-h-[calc(100vh-0.25rem)] supports-[height:100dvh]:max-h-[calc(100dvh-0.25rem)] md:max-h-[calc(100dvh-1rem)] w-full max-w-none overflow-y-auto overscroll-contain rounded-t-2xl shadow-2xl md:max-h-none md:w-[calc(100%-2rem)] md:max-w-md md:overflow-visible md:rounded-box"
            restoreFocus={false}
        >
            {/* Montado só enquanto aberto: cada abertura recomeça o formulário. */}
            {isOpen && <StoreSwitcherForm required={required} onClose={close} />}
        </Modal>
    );
}
